"use client"

import React, { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { ShieldCheck, Loader2, X, Calendar, MapPin, FileText, Check, ArrowRight, Package, Truck } from "lucide-react"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { getAllPorts } from "../../../services/portApi"
import type { Port } from "../../../types/port"
import { taskAPI } from "../../../services/taskApi"

interface SendToPCCResult {
  pccLocationId: number
  pccLocationName: string
  pccLocationAddress?: string
  destinationType: 'pcc'
  destinationId: number
}

interface SendToPCCModalProps {
  open: boolean
  onClose: () => void
  taskId: number
  taskNumber: string
  containerNumber: string
  onSuccess: (result: SendToPCCResult) => void
}

export function SendToPCCModal({
  open,
  onClose,
  taskId,
  taskNumber,
  containerNumber,
  onSuccess,
}: SendToPCCModalProps) {
  const [loading, setLoading] = useState(false)
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [locations, setLocations] = useState<Port[]>([])
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null)
  const [reason, setReason] = useState("")
  const [scheduledDeliveryTime, setScheduledDeliveryTime] = useState("")
  const [error, setError] = useState("")

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) {
      onClose()
    }
  }, [loading, onClose])

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handleKeyDown])

  useEffect(() => {
    if (open) {
      loadLocations()
    }
  }, [open])

  const loadLocations = async () => {
    setLoadingLocations(true)
    setError("")
    try {
      const response = await getAllPorts({ category: 'cbp', status: 'active' })
      const data = response.data || []
      setLocations(data)
      if (data.length === 0) {
        setError("No active CBP locations available")
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load CBP locations")
    } finally {
      setLoadingLocations(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedLocationId) {
      setError("Please select a CBP location")
      return
    }

    setLoading(true)
    setError("")
    try {
      await taskAPI.sendToPCC(
        taskId,
        selectedLocationId,
        reason.trim() || undefined,
        scheduledDeliveryTime || undefined
      )

      const selectedLocation = locations.find(l => Number(l.id) === selectedLocationId)

      const result: SendToPCCResult = {
        pccLocationId: selectedLocationId,
        pccLocationName: selectedLocation?.portName || 'CBP',
        pccLocationAddress: selectedLocation?.address?.street,
        destinationType: 'pcc',
        destinationId: selectedLocationId
      }
      onSuccess(result)
      handleClose()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } }; message?: string }
      setError(axiosErr.response?.data?.message || axiosErr.message || "Failed to send to CBP")
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedLocationId(null)
    setReason("")
    setScheduledDeliveryTime("")
    setError("")
    onClose()
  }

  if (!open) return null

  const modalContent = (
    <>
      {/* z-[100]：必须高过详情弹窗的 z-[70]，否则被它的黑底盖住＝点了没反应 */}
      <div
        className="fixed inset-0 z-[100] bg-black/50"
        onClick={handleClose}
      />
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">Send to CBP</h3>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5 max-h-[calc(90vh-140px)] overflow-y-auto">
          {/* Task Info - compact inline */}
          <div className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-lg">
            <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-medium text-sm text-gray-900 truncate">{taskNumber}</span>
              <span className="text-gray-300">·</span>
              <span className="text-sm text-gray-500 truncate">{containerNumber}</span>
            </div>
          </div>

          {/* CBP Location Selection */}
          <div className="space-y-2.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <MapPin className="h-3.5 w-3.5 text-gray-400" />
              Select CBP Location <span className="text-red-500">*</span>
            </Label>
            {loadingLocations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
                <span className="ml-2 text-sm text-gray-500">Loading CBP locations...</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[180px] overflow-y-auto pr-1">
                {locations.map((loc) => {
                  const isSelected = selectedLocationId === Number(loc.id)
                  const primaryContact = loc.contacts?.find(c => c.isPrimary) || loc.contacts?.[0]
                  return (
                    <div
                      key={loc.id}
                      onClick={() => setSelectedLocationId(Number(loc.id))}
                      className={`relative p-3 border rounded-lg cursor-pointer transition-all ${
                        isSelected
                          ? "border-amber-500 bg-amber-50/50 ring-1 ring-amber-500/20"
                          : "border-gray-200 hover:border-amber-300 hover:bg-amber-50/20"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        {/* Radio indicator */}
                        <div className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                          isSelected
                            ? "border-amber-500 bg-amber-500"
                            : "border-gray-300"
                        }`}>
                          {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm text-gray-900">{loc.portName}{loc.shortName && loc.shortName !== loc.portName ? ` (${loc.shortName})` : ''}</p>
                          {loc.address?.street && (
                            <p className="text-xs text-gray-500 mt-0.5">{loc.address.street}</p>
                          )}
                          {primaryContact && (
                            <p className="text-xs text-gray-400 mt-1">
                              {primaryContact.name}{primaryContact.phone ? ` · ${primaryContact.phone}` : ''}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <FileText className="h-3.5 w-3.5 text-gray-400" />
              Reason
              <span className="text-xs font-normal text-gray-400 ml-1">Optional</span>
            </Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Customs inspection required for this container"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg resize-none h-[72px] text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-colors placeholder:text-gray-400"
            />
          </div>

          {/* Scheduled Delivery Time */}
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              Scheduled Delivery
              <span className="text-xs font-normal text-gray-400 ml-1">Optional</span>
            </Label>
            <Input
              type="datetime-local"
              value={scheduledDeliveryTime}
              onChange={(e) => setScheduledDeliveryTime(e.target.value)}
              className="w-full text-sm"
            />
            <p className="text-xs text-gray-400">
              When should the container be delivered from CBP to warehouse?
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">
              <span className="flex-shrink-0 mt-0.5">⚠</span>
              <span>{error}</span>
            </div>
          )}

          {/* Flow Steps - visual stepper */}
          <div className="bg-amber-50/60 rounded-lg p-3.5 border border-amber-100">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2.5">What happens next</p>
            <div className="flex items-center gap-1 text-xs text-amber-600">
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-amber-200 font-medium whitespace-nowrap">
                <Truck className="h-3 w-3" /> Route to CBP
              </span>
              <ArrowRight className="h-3 w-3 text-amber-400 flex-shrink-0" />
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-amber-200 font-medium whitespace-nowrap">
                <ShieldCheck className="h-3 w-3" /> Inspection
              </span>
              <ArrowRight className="h-3 w-3 text-amber-400 flex-shrink-0" />
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded-md border border-amber-200 font-medium whitespace-nowrap">
                <Package className="h-3 w-3" /> Auto Delivery Task
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !selectedLocationId}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Processing...
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4 mr-1.5" />
                Send to CBP
              </>
            )}
          </Button>
        </div>
      </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}
